export type StorylineDirection = 'bullish' | 'bearish' | 'mixed' | 'neutral';
export type StorylineEventSentiment = 'positive' | 'negative' | 'neutral';

export interface StorylineChartEvent {
  date: string;
  label: string;
  summary: string;
  sentiment: StorylineEventSentiment;
  percentChange?: number | null;
  primaryCatalyst?: string | null;
  glp1Context?: string | null;
  citations?: Array<{ url: string; title: string }> | null;
}

export interface StorylineChartSymbol {
  symbol: string;
  label: string;
  /** When the display symbol differs from the price API ticker (e.g. SIXV → XLV). */
  priceSymbol?: string;
}

export interface MarketStoryline {
  id: string;
  rank: number;
  title: string;
  direction: StorylineDirection;
  summary: string;
  body: string;
  tags: string[];
  tickers?: string[];
  chartSymbol: string;
  chartLabel: string;
  /** When set, renders a grid of stock charts instead of a single sector chart. */
  chartSymbols?: StorylineChartSymbol[];
  events: StorylineChartEvent[];
}

export interface MarketStorylinesPayload {
  as_of: string;
  as_of_label: string;
  storylines: MarketStoryline[];
}

/** Curated market narratives — updated to reflect conditions as of mid-July 2026. */
export const MARKET_STORYLINES: MarketStorylinesPayload = {
  as_of: '2026-07-14',
  as_of_label: 'Tuesday, July 14, 2026',
  storylines: [
    {
      id: 'ai-bubble',
      rank: 1,
      title: 'AI Bubble',
      direction: 'mixed',
      summary:
        'Hyperscaler capex and AI infrastructure spending keep lifting the Nasdaq, but valuation dispersion and legacy software cracks are raising bubble-risk questions.',
      body: `The AI trade remains the dominant equity narrative in 2026. Cloud and chip leaders continue to absorb massive capex budgets as enterprises race to deploy models and data-center capacity.

At the same time, investors are increasingly sensitive to signs of overbuild: IBM's 25% plunge on a software-to-infrastructure transition warning showed how quickly sentiment can punish names that look mispositioned. Strategists debate whether current multiples embed a decade of growth or whether productivity gains will justify the spend.

The bull case rests on real revenue adoption; the bear case is that capex runs ahead of monetization and multiples compress before earnings catch up.`,
      tags: ['AI', 'Tech', 'Valuation'],
      tickers: ['NVDA', 'MSFT', 'GOOGL', 'META', 'SVCO'],
      chartSymbol: 'QQQ',
      chartLabel: 'Nasdaq-100 ETF (QQQ)',
      events: [
        {
          date: '2026-05-28',
          label: 'Megacap earnings beat',
          summary:
            'Hyperscaler results reinforced AI revenue traction, pushing the Nasdaq to fresh highs as investors leaned into the capex supercycle narrative.',
          sentiment: 'positive',
        },
        {
          date: '2026-06-18',
          label: 'Rate scare hits growth',
          summary:
            'A hawkish Fed dot plot repriced long-duration tech multiples lower, reminding the market that AI leaders are still rate-sensitive.',
          sentiment: 'negative',
        },
        {
          date: '2026-07-14',
          label: 'IBM −25% warning',
          summary:
            'IBM plunged after flagging a painful shift from software licenses to data-center spend — a cautionary tale for AI-exposed names with legacy exposure.',
          sentiment: 'negative',
        },
        {
          date: '2026-07-14',
          label: 'Nasdaq rebounds',
          summary:
            'Despite IBM, softer CPI pulled yields lower and the Nasdaq rallied 0.9%, showing dip-buying appetite for core AI beneficiaries remains intact.',
          sentiment: 'positive',
        },
      ],
    },
    {
      id: 'iran-conflict',
      rank: 2,
      title: 'Iran Conflict',
      direction: 'mixed',
      summary:
        'Renewed US-Iran hostilities and Strait of Hormuz rhetoric keep oil elevated and cap equity upside even when other catalysts turn positive.',
      body: `Geopolitical risk in the Middle East has re-emerged as a first-order market driver. Military strikes, missile exchanges, and sovereignty rhetoric around the Strait of Hormuz have pushed Brent toward the mid-$80s.

Energy equities have led on supply-disruption pricing, while airlines, shippers, and import-dependent economies face margin pressure. Equities have partially looked through the headlines — but strategists warn the conflict is a lingering overhang, especially if oil re-accelerates inflation.

The key variable is duration: a contained flare-up is absorbable; a prolonged blockade scenario would feed directly into CPI and Fed policy.`,
      tags: ['Geopolitics', 'Energy', 'Oil'],
      tickers: ['XLE', 'USO', 'CVX', 'LMT'],
      chartSymbol: 'XLE',
      chartLabel: 'Energy Select Sector SPDR (XLE)',
      events: [
        {
          date: '2026-06-12',
          label: 'Ceasefire pullback',
          summary:
            'A fragile US-Iran ceasefire eased gasoline prices and pulled energy equities lower as near-term supply fears faded.',
          sentiment: 'negative',
        },
        {
          date: '2026-06-30',
          label: 'Hormuz rhetoric',
          summary:
            'Iran signaled sovereignty over the Strait of Hormuz, reviving the crude risk premium and lifting XLE into July.',
          sentiment: 'positive',
        },
        {
          date: '2026-07-13',
          label: 'Oil spike (+9%)',
          summary:
            'Renewed US-Iran strikes sent Brent up roughly 9% in a single session as traders priced prolonged supply disruption risk.',
          sentiment: 'positive',
        },
        {
          date: '2026-07-14',
          label: 'Strait standoff',
          summary:
            'Hostilities continued with Brent settling near $84; energy leadership held even as broader equities recovered on softer CPI.',
          sentiment: 'neutral',
        },
      ],
    },
    {
      id: 'inflation-fed-tightening',
      rank: 3,
      title: 'Inflation and Fed Tightening',
      direction: 'mixed',
      summary:
        'Inflation has cooled from spring highs but remains above target; Chair Warsh is holding a hawkish line and markets still price risk of further tightening.',
      body: `Macro markets remain caught between improving headline inflation and a Fed that refuses to declare victory. June CPI came in at 3.5% y/y — better than feared — but core components and energy volatility keep policymakers cautious.

Chair Kevin Warsh has emphasized that the 2% inflation target is non-negotiable and that the June rate hold is not a pivot. Dot plots still lean toward hikes, and the "Fed put" has been repriced further out of the money.

For portfolios, the tension is familiar: softer prints support duration and growth, but any oil-driven inflation rebound or sticky services could revive tightening fears quickly.`,
      tags: ['Macro', 'Inflation', 'Fed'],
      tickers: ['SPY', 'TLT', 'TIP'],
      chartSymbol: 'SPY',
      chartLabel: 'S&P 500 ETF (SPY)',
      events: [
        {
          date: '2026-05-15',
          label: 'Hot May CPI',
          summary:
            'Headline CPI surged 4.2% y/y, reigniting rate-hike fears and pressuring duration-sensitive equities.',
          sentiment: 'negative',
        },
        {
          date: '2026-06-18',
          label: 'FOMC holds hawkish',
          summary:
            'The Fed kept rates at 3.5%–3.75% with a dot plot leaning toward hikes, leaving risk assets range-bound.',
          sentiment: 'neutral',
        },
        {
          date: '2026-07-14',
          label: 'Warsh testimony',
          summary:
            'Chair Warsh told Congress the inflation target is "unambiguous and unanimous," reinforcing restrictive policy rhetoric.',
          sentiment: 'negative',
        },
        {
          date: '2026-07-14',
          label: 'CPI relief rally',
          summary:
            'June CPI cooled to 3.5% y/y versus 3.9% expected, pulling yields lower and sparking a broad equity rebound.',
          sentiment: 'positive',
        },
      ],
    },
    {
      id: 'regional-banking-crisis',
      rank: 4,
      title: 'Regional Banking Crisis',
      direction: 'bearish',
      summary:
        'Smaller banks remain under pressure from commercial real estate exposure, deposit competition, and higher funding costs — even as money-center banks report record profits.',
      body: `The regional banking stress theme never fully went away after 2023. Smaller institutions still carry outsized commercial real estate books, face deposit-beta pressure, and must compete with money-market yields for funding.

The contrast with Q2 2026 money-center earnings is stark: JPMorgan hit record profits while regional indices lag. Investors differentiate between trading-heavy giants with fortress balance sheets and regionals with CRE concentration and rate-sensitive loan books.

The risk is contagion through confidence channels rather than a repeat of March 2023 — but credit tightening at smaller banks still matters for local business lending and housing markets.`,
      tags: ['Banks', 'CRE', 'Credit'],
      tickers: ['KRE', 'ZION', 'PACW', 'JPM'],
      chartSymbol: 'KRE',
      chartLabel: 'SPDR S&P Regional Banking ETF (KRE)',
      events: [
        {
          date: '2026-03-10',
          label: 'CRE markdown fears',
          summary:
            'Renewed focus on office-loan losses pressured regional bank shares as investors repriced credit reserves higher.',
          sentiment: 'negative',
        },
        {
          date: '2026-05-01',
          label: 'Deposit competition',
          summary:
            'Regional banks reported elevated funding costs as customers shifted balances to higher-yielding alternatives.',
          sentiment: 'negative',
        },
        {
          date: '2026-07-10',
          label: 'Megabank strength',
          summary:
            'JPMorgan and peers posted record profits, widening the performance gap between money-center and regional banks.',
          sentiment: 'neutral',
        },
        {
          date: '2026-07-14',
          label: 'KRE lags XLF',
          summary:
            'Regional banking indices underperformed large-cap financials as the market rewarded scale, trading revenue, and balance-sheet quality.',
          sentiment: 'negative',
        },
      ],
    },
    {
      id: 'glp1-disruption',
      rank: 5,
      title: 'GLP-1 Disruption',
      direction: 'mixed',
      summary:
        'Weight-loss drugs continue to reshape healthcare, food, and med-device demand — lifting GLP-1 manufacturers while pressuring legacy obesity-adjacent franchises.',
      body: `GLP-1 agonists remain one of the most disruptive product cycles in healthcare. Eli Lilly and Novo Nordisk continue to expand supply and indications, while insurers grapple with coverage costs and employers weigh pharmacy budgets.

The ripple effects are broad: food and beverage names face demand questions, med-device companies tied to obesity surgery see procedure mix shifts, and fitness/retail narratives adjust to a world of pharmacological weight management.

Investors are past the initial hype phase and into a stock-picking regime — winners are those with pricing power and supply; losers are businesses whose volumes assumed pre-GLP-1 consumption patterns.`,
      tags: ['Healthcare', 'Pharma', 'Consumer'],
      tickers: ['OZEM', 'SIXV', 'LLY', 'NVO', 'RMD', 'INSP'],
      chartSymbol: 'SIXV',
      chartLabel: 'Healthcare Index (SIXV)',
      chartSymbols: [
        { symbol: 'OZEM', label: 'Roundhill GLP-1 ETF (OZEM)' },
        { symbol: 'SIXV', label: 'Healthcare Index (SIXV)', priceSymbol: 'XLV' },
        { symbol: 'LLY', label: 'Eli Lilly (LLY)' },
        { symbol: 'NVO', label: 'Novo Nordisk (NVO)' },
        { symbol: 'RMD', label: 'ResMed (RMD)' },
        { symbol: 'INSP', label: 'Inspire Medical (INSP)' },
      ],
      events: [
        {
          date: '2021-06-04',
          label: 'Wegovy obesity approval',
          summary: 'FDA approved Wegovy for chronic weight management, kicking off the GLP-1 obesity trade.',
          sentiment: 'positive',
        },
        {
          date: '2022-04-28',
          label: 'SURMOUNT-1 tirzepatide data',
          summary: 'Lilly tirzepatide obesity data reinforced the competitive GLP-1 narrative.',
          sentiment: 'positive',
        },
        {
          date: '2023-08-08',
          label: 'SELECT cardiovascular win',
          summary: 'SELECT trial showed semaglutide cardiovascular benefit beyond glucose control.',
          sentiment: 'positive',
        },
        {
          date: '2023-10-06',
          label: 'Food-stock GLP-1 fears',
          summary: 'Packaged food and snacking names sold off on GLP-1 demand concerns.',
          sentiment: 'negative',
        },
        {
          date: '2023-10-11',
          label: 'Dialysis-stock selloff',
          summary: 'Dialysis providers fell as investors modeled lower obesity-related comorbidity volumes.',
          sentiment: 'negative',
        },
        {
          date: '2023-11-08',
          label: 'Zepbound approval',
          summary: 'FDA approved Zepbound (tirzepatide) for obesity, widening the branded GLP-1 field.',
          sentiment: 'positive',
        },
        {
          date: '2023-11-13',
          label: 'SELECT full-data / device relief',
          summary: 'Full SELECT data and device names rallied on eased GLP-1 competitive fears.',
          sentiment: 'positive',
        },
        {
          date: '2024-03-05',
          label: 'Dialysis relief rally',
          summary: 'Dialysis stocks bounced as GLP-1 disruption fears moderated.',
          sentiment: 'positive',
        },
        {
          date: '2024-03-08',
          label: 'Wegovy cardiovascular label',
          summary: 'Wegovy won a cardiovascular risk-reduction label expansion.',
          sentiment: 'positive',
        },
        {
          date: '2024-12-20',
          label: 'CagriSema disappointment / Novo crash',
          summary: 'Novo sold off after CagriSema trial data disappointed versus elevated expectations.',
          sentiment: 'negative',
        },
        {
          date: '2025-04-17',
          label: 'Lilly oral GLP-1 upside',
          summary: 'Lilly oral GLP-1 pipeline updates lifted the stock on obesity market share hopes.',
          sentiment: 'positive',
        },
        {
          date: '2025-07-29',
          label: 'Novo profit warning',
          summary: 'Novo Nordisk warned on profits and U.S. pricing pressure, hitting the GLP-1 complex.',
          sentiment: 'negative',
        },
        {
          date: '2025-08-07',
          label: 'Lilly oral obesity disappointment',
          summary: 'Lilly oral obesity data underwhelmed, pressuring shares after prior optimism.',
          sentiment: 'negative',
        },
        {
          date: '2025-11-06',
          label: 'U.S. pricing/access reset',
          summary: 'Policy and pricing headlines reset GLP-1 access assumptions across the trade.',
          sentiment: 'neutral',
        },
      ],
    },
    {
      id: 'ev-boom-slowdown',
      rank: 6,
      title: 'EV Boom and Slowdown',
      direction: 'mixed',
      summary:
        'Electric vehicle adoption continues long-term but near-term growth has cooled — price wars, subsidy shifts, and demand normalization are repricing the sector.',
      body: `The EV narrative has shifted from hypergrowth to digestion. After years of rapid adoption curves, 2025–2026 brought price competition, inventory builds, and slower unit growth in key markets.

Tesla and legacy OEM EV lines face margin pressure as they chase volume. Charging infrastructure buildout continues, but investor focus has moved to free cash flow and whether subsidies and China competition compress returns.

The long-term electrification trend remains intact; the market is punishing assumptions that linear growth would continue without cyclical pauses.`,
      tags: ['Autos', 'EV', 'Consumer'],
      tickers: ['TSLA', 'RIVN', 'LCID', 'XLY'],
      chartSymbol: 'TSLA',
      chartLabel: 'Tesla Inc (TSLA)',
      events: [
        {
          date: '2026-04-02',
          label: 'Q1 deliveries miss',
          summary:
            'Tesla deliveries came in below consensus, reigniting debate over EV demand saturation and pricing power.',
          sentiment: 'negative',
        },
        {
          date: '2026-05-15',
          label: 'Price cut cycle',
          summary:
            'Automakers renewed EV price competition in China and Europe, pressuring margins across the group.',
          sentiment: 'negative',
        },
        {
          date: '2026-06-20',
          label: 'Robotaxi narrative',
          summary:
            'Tesla rallied on autonomy optimism as investors weighed long-term optionality against near-term volume headwinds.',
          sentiment: 'positive',
        },
        {
          date: '2026-07-14',
          label: 'Mixed EV tape',
          summary:
            'EV leaders traded divergently — autonomy hopes vs. margin reality — as the sector digested slower industry-wide unit growth.',
          sentiment: 'neutral',
        },
      ],
    },
    {
      id: 'semiconductor-cycle',
      rank: 7,
      title: 'Semiconductor Cycle',
      direction: 'bullish',
      summary:
        'AI-driven demand is extending the upcycle for chips and equipment, though memory pricing and non-AI end markets remain uneven.',
      body: `Semiconductors sit at the center of the AI industrialization story. GPU, networking, and custom silicon demand from hyperscalers has extended what looked like a late-cycle environment into a prolonged upcycle for leading-edge names.

Equipment and foundry capacity remain tight at the frontier; memory and automotive chips tell a more mixed story. Investors watch inventory weeks, HBM pricing, and whether enterprise AI adoption broadens beyond the cloud giants.

The SOX complex has outperformed when capex guidance stays firm and underperformed when investors fear double-ordering or a 2027 digestion phase.`,
      tags: ['Semis', 'AI', 'Capex'],
      tickers: ['NVDA', 'AMD', 'AVGO', 'SMH', 'SVCO'],
      chartSymbol: 'XLK',
      chartLabel: 'Technology Select Sector SPDR (XLK)',
      events: [
        {
          date: '2026-05-28',
          label: 'NVDA guidance strong',
          summary:
            'Nvidia data-center outlook reinforced the AI semiconductor upcycle, lifting chip and equipment names.',
          sentiment: 'positive',
        },
        {
          date: '2026-06-05',
          label: 'Hyperscaler capex',
          summary:
            'Cloud capex guidance reaffirmed multi-year AI infrastructure spend, supporting the semi complex.',
          sentiment: 'positive',
        },
        {
          date: '2026-07-08',
          label: 'Semis leadership',
          summary:
            'Chip and networking names outperformed as investors favored AI picks-and-shovels over legacy software.',
          sentiment: 'positive',
        },
        {
          date: '2026-07-14',
          label: 'IBM rotation',
          summary:
            'Software-to-infrastructure spending shifts highlighted the winners-and-losers dynamic within the broader tech/semi ecosystem.',
          sentiment: 'neutral',
        },
      ],
    },
    {
      id: 'commercial-real-estate-stress',
      rank: 8,
      title: 'Commercial Real Estate Stress',
      direction: 'bearish',
      summary:
        'Office vacancies, refinancing walls, and higher-for-longer rates keep CRE — especially urban office — under structural pressure.',
      body: `Commercial real estate remains one of the slow-burn risks in the financial system. Office occupancy in major metros is still below pre-pandemic norms, refinancing maturities loom, and cap rates have adjusted higher in a restrictive rate environment.

REIT indices reflect the bifurcation: data centers and logistics hold up; office and some retail struggle. The stress transmits into regional bank loan books, CMBS spreads, and municipal tax bases tied to commercial property values.

Markets have largely moved this from acute crisis to chronic headwind — but periodic CRE headlines still spike volatility in financials and real estate ETFs.`,
      tags: ['CRE', 'REITs', 'Office'],
      tickers: ['VNQ', 'BXP', 'SLG', 'XLRE'],
      chartSymbol: 'VNQ',
      chartLabel: 'Vanguard Real Estate ETF (VNQ)',
      events: [
        {
          date: '2026-03-10',
          label: 'Office vacancy peak',
          summary:
            'Major-market office vacancy data renewed concerns about cash flows on maturing loans, pressuring office REITs.',
          sentiment: 'negative',
        },
        {
          date: '2026-04-15',
          label: 'Refinancing wall',
          summary:
            'Analysts flagged a wave of CRE loan maturities at higher rates, widening CMBS spreads and hitting VNQ.',
          sentiment: 'negative',
        },
        {
          date: '2026-06-18',
          label: 'Rates stay elevated',
          summary:
            'The Fed hawkish hold reinforced cap-rate pressure on levered property owners with near-term maturities.',
          sentiment: 'negative',
        },
        {
          date: '2026-07-14',
          label: 'Bifurcated REITs',
          summary:
            'Data-center and logistics REITs outperformed office-exposed names, keeping the sector index range-bound.',
          sentiment: 'neutral',
        },
      ],
    },
    {
      id: 'tariffs-trade-policy',
      rank: 9,
      title: 'Tariffs and Trade Policy',
      direction: 'mixed',
      summary:
        'New and threatened tariffs on imports are reshaping supply chains, margin assumptions, and the relative performance of domestic vs. global-facing industrials.',
      body: `Trade policy has returned as a live market variable in 2026. Tariff announcements and retaliation threats create headline volatility for multinationals, importers, and companies with China-heavy supply chains.

Domestic manufacturers and reshoring beneficiaries can see tailwinds, while retailers, auto suppliers, and tech hardware names face cost pressure. Markets often fade the first move, then reprice when guidance confirms margin impact.

Investors are watching policy implementation timelines, exemption lobbying, and whether tariff pass-through shows up in PPI and CPI prints — linking this storyline directly to the inflation and Fed narrative.`,
      tags: ['Trade', 'Policy', 'Industrials'],
      tickers: ['XLI', 'CAT', 'DE', 'AAPL', 'SVCO'],
      chartSymbol: 'XLI',
      chartLabel: 'Industrial Select Sector SPDR (XLI)',
      events: [
        {
          date: '2026-04-02',
          label: 'Tariff escalation',
          summary:
            'A new round of import duties sparked a risk-off session for global supply-chain names and lifted domestic industrial beneficiaries.',
          sentiment: 'negative',
        },
        {
          date: '2026-05-20',
          label: 'Exemption hopes',
          summary:
            'Rumors of sector carve-outs eased pressure on tech hardware and auto suppliers, helping XLI recover part of its tariff-driven drawdown.',
          sentiment: 'positive',
        },
        {
          date: '2026-06-25',
          label: 'PPI pass-through',
          summary:
            'Producer price data showed early signs of tariff-related input cost pressure, reviving concerns about second-round inflation effects.',
          sentiment: 'negative',
        },
        {
          date: '2026-07-14',
          label: 'Policy uncertainty',
          summary:
            'Ongoing trade rhetoric kept industrials range-bound as investors weighed reshoring tailwinds against margin compression risks.',
          sentiment: 'neutral',
        },
      ],
    },
    {
      id: 'market-concentration',
      rank: 10,
      title: 'Market Concentration',
      direction: 'mixed',
      summary:
        'A handful of megacap names still drive index-level returns, widening the gap between cap-weighted benchmarks and equal-weight breadth.',
      body: `Market concentration remains a defining structural feature of 2026. The largest technology and platform companies still account for an outsized share of S&P 500 and Nasdaq returns, even as equal-weight indices show more modest gains.

The bull interpretation is that winners keep winning on durable earnings power and AI monetization. The bear interpretation is fragile index-level performance — a few names sneeze and the headline index catches cold.

Investors track equal-weight vs. cap-weight spreads, breadth indicators, and whether rotation into financials, industrials, and small caps signals a healthier broadening phase or a temporary trade.`,
      tags: ['Breadth', 'Megacap', 'Indices'],
      tickers: ['SPY', 'RSP', 'AAPL', 'MSFT'],
      chartSymbol: 'SPY',
      chartLabel: 'S&P 500 ETF (SPY)',
      events: [
        {
          date: '2026-05-28',
          label: 'Megacap drives index',
          summary:
            'Cap-weighted S&P gains were almost entirely attributable to a handful of AI-linked megacaps while equal-weight breadth lagged.',
          sentiment: 'neutral',
        },
        {
          date: '2026-06-18',
          label: 'Narrow rally warning',
          summary:
            'Strategists flagged record concentration levels as a risk factor if megacap earnings disappoint or rates reprice higher.',
          sentiment: 'negative',
        },
        {
          date: '2026-07-10',
          label: 'Financials broaden tape',
          summary:
            'Strong bank earnings temporarily improved market breadth as non-megacap sectors contributed more to index gains.',
          sentiment: 'positive',
        },
        {
          date: '2026-07-14',
          label: 'Concentration persists',
          summary:
            'Despite broader participation on the CPI rally day, year-to-date index returns remained heavily skewed toward the largest names.',
          sentiment: 'neutral',
        },
      ],
    },
  ],
};

export function getStorylineById(id: string): MarketStoryline | undefined {
  return MARKET_STORYLINES.storylines.find((s) => s.id === id);
}

export function getStorylineByRank(rank: number): MarketStoryline | undefined {
  return MARKET_STORYLINES.storylines.find((s) => s.rank === rank);
}

/** Storylines that list this ticker in `tickers` or `chartSymbols`. */
export function getStorylinesForTicker(ticker: string): MarketStoryline[] {
  const upper = ticker.trim().toUpperCase();
  if (!upper) return [];
  return MARKET_STORYLINES.storylines
    .filter(
      (storyline) =>
        storyline.tickers?.some((t) => t.toUpperCase() === upper) ||
        storyline.chartSymbols?.some((chart) => chart.symbol.toUpperCase() === upper),
    )
    .sort((a, b) => a.rank - b.rank);
}
